import { TokenReissueResponse } from '@/apis/handler/auth/authSchema';
import axios from 'axios';
import { PROTECTED_PATH } from 'shared/constants';
import { getPathWithoutLang } from 'shared/lib/converter';
import { useInfoStore } from 'store/useInfoStore';

export const request = axios.create({
  baseURL: process.env.REACT_APP_API,
  timeout: 20000,
  headers: {
    Accept: 'application/json',
  },
  withCredentials: true,
});

// Helper function to detect language from URL path (for development)
const getLanguageFromPath = (): string => {
  const pathname = window.location.pathname;
  const languageCode = pathname.split('/')[1];

  switch (languageCode) {
    case 'zh':
      return 'zh-CN';
    case 'vi':
      return 'vi';
    case 'ja':
      return 'ja';
    default:
      return 'en-US';
  }
};

request.interceptors.request.use(
  (config) => {
    if (!config.headers.Authorization) {
      const { accessToken: latestToken } = useInfoStore.getState();
      if (latestToken) {
        config.headers.Authorization = `Bearer ${latestToken}`;
      }
    }

    // Add Accept-Language header
    const acceptLanguage = getLanguageFromPath();
    config.headers['Accept-Language'] = acceptLanguage;

    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);

request.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      originalRequest._retry = true;
      try {
        const { data } = await axios.post<TokenReissueResponse>(
          `${process.env.REACT_APP_API}/auth/token/reissue/member`,
          {},
          { withCredentials: true },
        );

        // NOTE: http code is returned as 200 even if the request is failed. so we need to check isSuccess
        // NOTE: 1. refresh  토큰이 만료되면 아래 조건문이 실행됩니다.
        if (!data.isSuccess) {
          useInfoStore.getState().resetInfo?.();

          // NOTE: user should be logged out even when token is expired
          // NOTE: 2. refresh 토큰이 만료된 상태에서 로그아웃 요청을 보내면 400번대 에러가 발생하기 때문에 에러를 던지게 됩니다
          await axios.post(`${process.env.REACT_APP_API}/auth/logout`);
        }

        const newAccessToken = data.result.accessToken;

        useInfoStore.getState().setInfo({ ...useInfoStore.getState().getInfo(), accessToken: newAccessToken });

        originalRequest.headers.Authorization = `Bearer ${newAccessToken}`;

        return axios(originalRequest);
      } catch (refreshError) {
        // NOTE: 3. 에러를 전달받고, 메인 페이지로 이동하도록 합니다
        console.error('refreshError', refreshError);

        // 보호된 경로에서 접근한 경우 redirect 파라미터와 함께 홈으로 이동
        const pathname = window.location.pathname;

        const pathWithoutLang = getPathWithoutLang(pathname);
        const isProtectedPath = PROTECTED_PATH.some((path) => pathWithoutLang.startsWith(path));

        if (isProtectedPath) {
          // 보호된 경로인 경우 redirect 파라미터와 함께 홈으로 이동
          window.location.href = `/?redirect=${encodeURIComponent(pathname)}`;
        } else {
          // NOTE: 500 에러인 경우 에러 바운더리에서 처리하도록 하기 위해 500 에러가 아닌 경우만 '/'로 이동
          // NOTE: 만약 '/'로 이동하면 다시 '/'로 이동하는 재귀에 빠지게 되는 문제 있음
          if (error.response?.status !== 500) {
            window.location.href = '/';
          }
        }
      }
    }
    return Promise.reject(error);
  },
);
